import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import moment from "moment";

import {
  useCreatePatientMedicalHistory,
  useDeletePatientMedicalHistory,
} from "features/patients/hooks/usePatientMedicalHistory";
import { useICD10Codes } from "hooks/useICD10Codes";

export const useIcdSearch = ({ consultationId, patientId }) => {
  const [icdSearch, setIcdSearch] = useState("");
  const [isIcdDropdownOpen, setIsIcdDropdownOpen] = useState(false);
  const icdSearchRef = useRef(null);

  const { data: icd10Codes, isLoading: isLoadingIcd } = useICD10Codes(icdSearch);
  const createMedicalHistory = useCreatePatientMedicalHistory();
  const deleteMedicalHistory = useDeletePatientMedicalHistory();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (icdSearchRef.current && !icdSearchRef.current.contains(event.target)) {
        setIsIcdDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleIcdSelect = async (code) => {
    if (!consultationId || !patientId) {
      toast.error("Missing consultation or patient information");
      return;
    }
    try {
      await createMedicalHistory.mutateAsync({
        patientId: parseInt(patientId),
        consultationId,
        diseaseCode: code.code,
        medicalDetails: `(${code.icdType}) - ${code.name}`,
        recordedDate: moment().format("YYYY-MM-DD"),
        diseaseDate: moment().format("YYYY-MM-DD"),
      });
      toast.success(`Added ${code.code} to medical history`);
      setIcdSearch("");
      setIsIcdDropdownOpen(false);
    } catch {
      toast.error("Failed to add ICD-10 code");
    }
  };

  const handleIcdRemove = async (historyItem) => {
    try {
      await deleteMedicalHistory.mutateAsync({
        id: historyItem.id,
        consultationId,
      });
      toast.success(`Removed ${historyItem.diseaseCode}`);
    } catch {
      toast.error("Failed to remove ICD-10 code");
    }
  };

  return {
    icdSearch,
    setIcdSearch,
    isIcdDropdownOpen,
    setIsIcdDropdownOpen,
    icdSearchRef,
    icd10Codes,
    isLoadingIcd,
    handleIcdSelect,
    handleIcdRemove,
    isDeletingMedicalHistory: deleteMedicalHistory.isPending,
  };
};
